/**
 * Internal hook for scheduled meetings API operations
 *
 * This hook is specific to ScheduledMeetingsContext and encapsulates
 * all API calls through apiManager.ownership.
 *
 * NOT for external use - components should use the public hooks from the context.
 */

import { useCallback } from 'react';
import { useApiManager } from '../../../hooks';
import type {
  GetScheduleListQuery,
  PatchScheduleRequest,
  PostScheduleRequest,
} from '../../../types/api';
import type { ScheduledMeetingsApiService } from '../types';

/**
 * Internal API operations for scheduled meetings
 */
const useScheduledMeetingsApi = (): ScheduledMeetingsApiService => {
  const apiManager = useApiManager();

  /**
   * Fetch list of scheduled meetings
   */
  const fetchMeetingsList = useCallback(
    async (query?: GetScheduleListQuery) => {
      return apiManager.ownership.getScheduleList(query);
    },
    [apiManager],
  );

  /**
   * Fetch single meeting details
   */
  const fetchMeetingDetail = useCallback(
    async (room: string, detailed?: boolean) => {
      return apiManager.ownership.getSchedule(room, detailed);
    },
    [apiManager],
  );

  /**
   * Create new meeting
   */
  const createMeeting = useCallback(
    async (request: PostScheduleRequest) => {
      return apiManager.ownership.createSchedule(request);
    },
    [apiManager],
  );

  /**
   * Update existing meeting
   */
  const updateMeeting = useCallback(
    async (request: PatchScheduleRequest) => {
      return apiManager.ownership.updateSchedule(request);
    },
    [apiManager],
  );

  /**
   * Delete meeting
   */
  const deleteMeeting = useCallback(
    async (room: string) => {
      return apiManager.ownership.deleteSchedule(room);
    },
    [apiManager],
  );

  /**
   * Check if user owns a meeting
   */
  const checkOwnership = useCallback(
    async (room: string): Promise<boolean> => {
      return apiManager.ownership
        .getScheduleInfo(room)
        .then((info) => info.role === 'owner')
        .catch(() => false);
    },
    [apiManager],
  );

  return {
    fetchMeetingsList,
    fetchMeetingDetail,
    createMeeting,
    updateMeeting,
    deleteMeeting,
    checkOwnership,
  };
};

export default useScheduledMeetingsApi;
